import { zodResolver } from '@hookform/resolvers/zod';
import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as z from 'zod';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
});

interface DemoUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface DemoData {
  users: DemoUser[];
}

type LoginFormValues = z.infer<typeof loginSchema>;

export const LoginPage = () => {
  const { t } = useTranslation();
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [demoData, setDemoData] = useState<DemoData | null>(null);

  useEffect(() => {
    // Fetch demo data if in dev mode or VITE_DEMO_MODE is true
    if (import.meta.env.DEV || import.meta.env.VITE_DEMO_MODE === 'true') {
      const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8787';
      fetch(`${apiBase}/api/dev/demo-data`)
        .then((res) => res.json())
        .then((data) => {
          if (!data || data.error || !data.users) return;
          setDemoData(data);
        })
        .catch((err) => console.error('Failed to fetch demo data:', err));
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormValues) => {
    try {
      const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8787';
      const response = await fetch(`${apiBase}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('auth.loginFailed'));
      }

      const result = await response.json();
      const token = result.data.accessToken;
      const user = result.data.user;

      Sentry.setUser({ id: user.id, email: user.email });
      localStorage.setItem('accessToken', token);
      localStorage.setItem('currentUser', JSON.stringify(user));

      window.location.href = '/dashboard';
    } catch (error: unknown) {
      console.error('Login failed:', error);
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert('An unexpected error occurred');
      }
    }
  };

  return (
    <Card className="w-full border-none shadow-xl shadow-blue-950/5 ring-1 ring-gray-200">
      <CardHeader className="space-y-1 pb-6">
        <CardTitle className="text-xl font-bold text-center">{t('auth.loginTitle')}</CardTitle>
        <CardDescription className="text-center">{t('auth.enterCredentials')}</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700">
              {t('auth.email')}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              className="h-10 border-gray-200 focus:ring-blue-500"
              {...register('email')}
            />
            {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Link to="/forgot-password" className="text-xs text-blue-600 hover:underline">
                {t('auth.forgotPassword')}
              </Link>
            </div>
            <Input id="password" type="password" className="h-10 border-gray-200" {...register('password')} />
            {errors.password && <p className="text-xs text-red-500">{errors.password.message}</p>}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-4 pb-8">
          <Button
            type="submit"
            className="w-full h-11 bg-blue-600 hover:bg-blue-700 font-bold transition-all"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('common.loading') : t('auth.login')}
          </Button>

          {demoData && (
            <div className="w-full mt-6 p-4 rounded-xl bg-orange-50/50 border border-orange-100">
              <p className="text-[10px] font-bold text-orange-400 uppercase tracking-widest mb-3 text-center">
                Developer Tool
              </p>
              <div className="space-y-3">
                <select
                  className="w-full text-sm border-gray-200 rounded-lg p-2.5 bg-white focus:ring-orange-500 focus:border-orange-500 shadow-sm"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  <option value="">-- {t('auth.selectUser')} --</option>
                  {demoData.users
                    .filter((u) => u.role === 'applicant')
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.email})
                      </option>
                    ))}
                </select>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-10 border-orange-200 text-orange-700 hover:bg-white hover:border-orange-300 font-medium"
                  disabled={!selectedUserId}
                  onClick={async () => {
                    try {
                      const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8787';
                      const res = await fetch(`${apiBase}/api/dev/login-as`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ userId: selectedUserId }),
                      });

                      if (res.ok) {
                        const { data } = await res.json();
                        localStorage.setItem('accessToken', data.accessToken);
                        localStorage.setItem('currentUser', JSON.stringify(data.user));
                        window.location.href = '/dashboard';
                      } else {
                        alert('Demo login failed');
                      }
                    } catch (e) {
                      console.error(e);
                      alert('Network error');
                    }
                  }}
                >
                  Quick Dev Login
                </Button>
              </div>
            </div>
          )}
        </CardFooter>
      </form>
    </Card>
  );
};
